/**
 * WebSocket Hook
 *
 * Generic WebSocket hook for real-time communication with the server.
 * Features:
 * - Automatic connection management
 * - Reconnection with exponential backoff
 * - Event subscription/unsubscription
 * - Cookie-based authentication (httpOnly cookies sent automatically by browser)
 * - Connection state tracking
 */

import { useState, useEffect, useCallback, useRef } from 'react';

// =============================================================================
// Types
// =============================================================================

/**
 * WebSocket connection state
 */
export type WebSocketState = 'connecting' | 'connected' | 'disconnected' | 'reconnecting';

/**
 * WebSocket message structure from server
 */
export interface WebSocketMessage<T = unknown> {
  /** Event type */
  event: string;
  /** Event payload */
  data: T;
  /** Optional timestamp */
  timestamp?: string;
}

/**
 * WebSocket event handler type
 */
export type WebSocketEventHandler<T = unknown> = (data: T) => void;

/**
 * WebSocket hook options
 */
export interface UseWebSocketOptions {
  /** WebSocket server URL (defaults to WS_URL env var or derived from current host) */
  url?: string;
  /** Auto-connect on mount */
  autoConnect?: boolean;
  /** Enable automatic reconnection */
  reconnect?: boolean;
  /** Maximum reconnection attempts (0 = infinite) */
  maxReconnectAttempts?: number;
  /** Base reconnection delay in ms */
  reconnectDelay?: number;
  /** Maximum reconnection delay in ms */
  maxReconnectDelay?: number;
  /** @deprecated Access token parameter is no longer used. Authentication uses httpOnly cookies sent automatically by the browser. */
  accessToken?: string | null;
  /** Protocols for WebSocket connection */
  protocols?: string | string[];
  /** Heartbeat interval in ms (0 = disabled) */
  heartbeatInterval?: number;
  /** Connection timeout in ms */
  connectionTimeout?: number;
}

/**
 * WebSocket hook return value
 */
export interface UseWebSocketReturn {
  /** Current connection state */
  state: WebSocketState;
  /** Whether the socket is connected */
  isConnected: boolean;
  /** Last error message */
  error: string | null;
  /** Connect to the WebSocket server */
  connect: () => void;
  /** Disconnect from the WebSocket server */
  disconnect: () => void;
  /** Send a message to the server */
  send: <T>(event: string, data: T) => boolean;
  /** Subscribe to an event */
  subscribe: <T>(event: string, handler: WebSocketEventHandler<T>) => () => void;
  /** Unsubscribe from an event */
  unsubscribe: (event: string, handler: WebSocketEventHandler) => void;
  /** Reconnection attempt count */
  reconnectCount: number;
}

// =============================================================================
// Constants
// =============================================================================

const DEFAULT_OPTIONS: Required<UseWebSocketOptions> = {
  url: '',
  autoConnect: true,
  reconnect: true,
  maxReconnectAttempts: 10,
  reconnectDelay: 1000,
  maxReconnectDelay: 30000,
  accessToken: null,
  protocols: [],
  heartbeatInterval: 30000,
  connectionTimeout: 10000,
};

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Get the default WebSocket URL based on current location
 */
function getDefaultWebSocketUrl(): string {
  // Check for environment variable first
  const envUrl = import.meta.env.VITE_WS_URL;
  if (envUrl) {
    return envUrl;
  }

  // Derive from current host
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const host = window.location.host;
  return `${protocol}//${host}/ws`;
}

/**
 * Calculate reconnection delay with exponential backoff and jitter
 */
function calculateReconnectDelay(
  attempt: number,
  baseDelay: number,
  maxDelay: number
): number {
  // Exponential backoff: baseDelay * 2^attempt
  const exponentialDelay = baseDelay * Math.pow(2, attempt);
  // Cap at maxDelay
  const cappedDelay = Math.min(exponentialDelay, maxDelay);
  // Add jitter (0-10% of delay)
  const jitter = cappedDelay * Math.random() * 0.1;
  return cappedDelay + jitter;
}

// =============================================================================
// Hook Implementation
// =============================================================================

/**
 * Custom hook for WebSocket connections with automatic reconnection
 *
 * Authentication is handled automatically via httpOnly cookies sent by the browser.
 * No manual token injection is required.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { state, isConnected, subscribe, send } = useWebSocket();
 *
 *   useEffect(() => {
 *     const unsubscribe = subscribe<BotStatus>('bot:status', (data) => {
 *       console.log('Bot status updated:', data);
 *     });
 *     return unsubscribe;
 *   }, [subscribe]);
 *
 *   return <div>Connection: {state}</div>;
 * }
 * ```
 */
export function useWebSocket(options: UseWebSocketOptions = {}): UseWebSocketReturn {
  // Merge options with defaults
  const opts = {
    ...DEFAULT_OPTIONS,
    ...options,
    url: options.url || getDefaultWebSocketUrl(),
  };

  // State
  const [state, setState] = useState<WebSocketState>('disconnected');
  const [error, setError] = useState<string | null>(null);
  const [reconnectCount, setReconnectCount] = useState(0);

  // Refs for mutable state
  const wsRef = useRef<WebSocket | null>(null);
  const eventHandlersRef = useRef<Map<string, Set<WebSocketEventHandler>>>(new Map());
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const heartbeatIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const connectionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isManualDisconnectRef = useRef(false);
  const reconnectAttemptRef = useRef(0);

  /**
   * Clear all timeouts and intervals
   */
  const clearTimers = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
    if (connectionTimeoutRef.current) {
      clearTimeout(connectionTimeoutRef.current);
      connectionTimeoutRef.current = null;
    }
  }, []);

  /**
   * Start heartbeat to keep connection alive
   */
  const startHeartbeat = useCallback(() => {
    if (opts.heartbeatInterval <= 0) return;

    heartbeatIntervalRef.current = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ event: 'ping', data: {} }));
      }
    }, opts.heartbeatInterval);
  }, [opts.heartbeatInterval]);

  /**
   * Handle incoming WebSocket message
   */
  const handleMessage = useCallback((event: MessageEvent) => {
    try {
      const message: WebSocketMessage = JSON.parse(event.data);
      const { event: eventType, data } = message;

      // Handle pong (heartbeat response)
      if (eventType === 'pong') {
        return;
      }

      // Get handlers for this event type
      const handlers = eventHandlersRef.current.get(eventType);
      if (handlers) {
        handlers.forEach((handler) => {
          try {
            handler(data);
          } catch (err) {
            // Handler errors should not crash the connection
          }
        });
      }

      // Also emit to wildcard handlers (for debugging)
      const wildcardHandlers = eventHandlersRef.current.get('*');
      if (wildcardHandlers) {
        wildcardHandlers.forEach((handler) => {
          try {
            handler(message);
          } catch (err) {
            // Handler errors should not crash the connection
          }
        });
      }
    } catch (err) {
      // Invalid JSON message - ignore
    }
  }, []);

  /**
   * Schedule a reconnection attempt
   */
  const scheduleReconnect = useCallback(() => {
    if (!opts.reconnect || isManualDisconnectRef.current) {
      return;
    }

    if (opts.maxReconnectAttempts > 0 && reconnectAttemptRef.current >= opts.maxReconnectAttempts) {
      setError(`Max reconnection attempts (${opts.maxReconnectAttempts}) reached`);
      setState('disconnected');
      return;
    }

    setState('reconnecting');
    const delay = calculateReconnectDelay(
      reconnectAttemptRef.current,
      opts.reconnectDelay,
      opts.maxReconnectDelay
    );

    reconnectTimeoutRef.current = setTimeout(() => {
      reconnectAttemptRef.current++;
      setReconnectCount(reconnectAttemptRef.current);
      // Trigger connect by updating state (will be handled in effect)
    }, delay);
  }, [opts.reconnect, opts.maxReconnectAttempts, opts.reconnectDelay, opts.maxReconnectDelay]);

  /**
   * Connect to the WebSocket server
   */
  const connect = useCallback(() => {
    // Clean up any existing connection
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    clearTimers();
    isManualDisconnectRef.current = false;
    setError(null);
    setState('connecting');

    try {
      const ws = new WebSocket(opts.url, opts.protocols);
      wsRef.current = ws;

      // Set up connection timeout
      connectionTimeoutRef.current = setTimeout(() => {
        if (ws.readyState !== WebSocket.OPEN) {
          ws.close();
          setError('Connection timeout');
          scheduleReconnect();
        }
      }, opts.connectionTimeout);

      ws.onopen = () => {
        clearTimeout(connectionTimeoutRef.current!);
        connectionTimeoutRef.current = null;
        reconnectAttemptRef.current = 0;
        setReconnectCount(0);
        setError(null);
        setState('connected');
        startHeartbeat();
      };

      ws.onclose = (event) => {
        clearTimers();

        if (isManualDisconnectRef.current) {
          setState('disconnected');
        } else if (!event.wasClean) {
          setError(`Connection closed unexpectedly (code: ${event.code})`);
          scheduleReconnect();
        } else {
          setState('disconnected');
        }
      };

      ws.onerror = () => {
        // Error details are not available in the browser for security reasons
        setError('WebSocket error occurred');
      };

      ws.onmessage = handleMessage;
    } catch (err) {
      setError(`Failed to create WebSocket connection: ${err instanceof Error ? err.message : 'Unknown error'}`);
      scheduleReconnect();
    }
  }, [opts.url, opts.protocols, opts.connectionTimeout, clearTimers, startHeartbeat, handleMessage, scheduleReconnect]);

  /**
   * Disconnect from the WebSocket server
   */
  const disconnect = useCallback(() => {
    isManualDisconnectRef.current = true;
    clearTimers();

    if (wsRef.current) {
      wsRef.current.close(1000, 'Manual disconnect');
      wsRef.current = null;
    }

    setState('disconnected');
    setError(null);
    reconnectAttemptRef.current = 0;
    setReconnectCount(0);
  }, [clearTimers]);

  /**
   * Send a message to the server
   */
  const send = useCallback(<T>(event: string, data: T): boolean => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      return false;
    }

    try {
      wsRef.current.send(JSON.stringify({ event, data }));
      return true;
    } catch (err) {
      return false;
    }
  }, []);

  /**
   * Subscribe to an event
   */
  const subscribe = useCallback(<T>(event: string, handler: WebSocketEventHandler<T>): () => void => {
    if (!eventHandlersRef.current.has(event)) {
      eventHandlersRef.current.set(event, new Set());
    }
    eventHandlersRef.current.get(event)!.add(handler as WebSocketEventHandler);

    // Return unsubscribe function
    return () => {
      const handlers = eventHandlersRef.current.get(event);
      if (handlers) {
        handlers.delete(handler as WebSocketEventHandler);
        if (handlers.size === 0) {
          eventHandlersRef.current.delete(event);
        }
      }
    };
  }, []);

  /**
   * Unsubscribe from an event
   */
  const unsubscribe = useCallback((event: string, handler: WebSocketEventHandler): void => {
    const handlers = eventHandlersRef.current.get(event);
    if (handlers) {
      handlers.delete(handler);
      if (handlers.size === 0) {
        eventHandlersRef.current.delete(event);
      }
    }
  }, []);

  /**
   * Auto-connect on mount if enabled
   */
  useEffect(() => {
    if (opts.autoConnect) {
      connect();
    }

    return () => {
      disconnect();
    };
    // Only run on mount/unmount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Handle reconnection state
   */
  useEffect(() => {
    if (state === 'reconnecting' && reconnectTimeoutRef.current) {
      // Wait for timeout to trigger reconnect
    }
  }, [state]);

  /**
   * Reconnect when access token changes (re-authenticate)
   *
   * @deprecated This effect is no longer needed as authentication uses httpOnly cookies.
   * The browser automatically sends updated cookies with each WebSocket connection.
   * This logic is preserved for backwards compatibility but has no effect.
   */
  useEffect(() => {
    if (opts.accessToken !== undefined && state === 'connected') {
      // Token changed while connected - reconnect to re-authenticate
      disconnect();
      connect();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts.accessToken]);

  return {
    state,
    isConnected: state === 'connected',
    error,
    connect,
    disconnect,
    send,
    subscribe,
    unsubscribe,
    reconnectCount,
  };
}

// =============================================================================
// Additional Types for Common Events
// =============================================================================

/**
 * Bot status update event data
 */
export interface BotStatusEvent {
  botId: string;
  status: 'STOPPED' | 'STARTING' | 'RUNNING' | 'STOPPING' | 'ERROR';
  pid?: number;
  error?: string;
  timestamp: string;
}

/**
 * Bot log event data
 */
export interface BotLogEvent {
  botId: string;
  level: 'debug' | 'info' | 'warn' | 'error';
  message: string;
  timestamp: string;
  metadata?: Record<string, unknown>;
}

/**
 * Node metrics event data
 */
export interface NodeMetricsEvent {
  nodeId: string;
  cpu: number;
  memory: number;
  gpu?: {
    utilization: number;
    memoryUsed: number;
    memoryTotal: number;
  };
  timestamp: string;
}

/**
 * Container status event data
 */
export interface ContainerStatusEvent {
  containerId: string;
  nodeId: string;
  status: 'STOPPED' | 'STARTING' | 'RUNNING' | 'STOPPING' | 'ERROR';
  timestamp: string;
}

/**
 * Container progress event data
 */
export interface ContainerProgressEvent {
  id: string;
  nodeId: string;
  containerId: string;
  type: 'model_download' | 'model_load' | 'container_startup';
  status: 'pending' | 'in_progress' | 'completed' | 'failed' | 'cancelled';
  model?: string;
  percentage: number;
  totalBytes: number;
  downloadedBytes: number;
  speedBps: number;
  etaSeconds: number;
  message?: string;
  error?: string;
  startedAt?: string;
  completedAt?: string;
  updatedAt: string;
}

/**
 * Container error event data
 */
export interface ContainerErrorEvent {
  id: string;
  nodeId: string;
  containerId?: string;
  containerName?: string;
  type: string;
  severity: 'info' | 'warning' | 'error' | 'critical';
  message: string;
  details?: Record<string, unknown>;
  logs?: string;
  exitCode?: number;
  resolved: boolean;
  resolvedAt?: string;
  occurredAt: string;
}

// =============================================================================
// Export Default
// =============================================================================

export default useWebSocket;
